import useStyles from '../../../../../style/js-style/containers/administration/calculator/CalculatorDialogStyle';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  CalculatorContext, ContextDirectField,
  getFlatSubFieldsOptions,
  getOrderedOptions, getTargetFieldOptionsForContextDirectField,
  getTargetFieldOptionsForContextModels,
  getTargetFieldOptionsForDocument,
  getTargetFieldOptionsForDossier,
  getTargetFieldOptionsForSourceVariable,
  getTargetFieldOptionsForThirdParty,
  isCalculationReferencedByTargetCalculation,
  isMatchingDossierTarget,
  isMatchingThirdPartyTarget,
  TargetFieldCategory,
  TargetFieldContext,
  VariableContext,
  VariableType,
} from '../../../../../services/common/CalculatorUtils';
import _ from 'lodash';
import { useFormContext, useWatch } from 'react-hook-form';
import CalculatorSelectInput from '../calculatorInputs/CalculatorSelectInput';
import i18next from 'i18next';
import DynamicSubTargetField from './DynamicSubTargetField';

const InnerTargetField = ({ fieldName, readOnly = false }) => {
  const { classes } = useStyles();

  const {
    selectedCalculation,
    countries,
    activities,
    thirdpartyAttributes,
    dossierAttributes,
    documentTypes,
    calculatedAttributes,
    thirdPartyIndicators,
    financialStrengthScores,
    financialStrengthRatings,
    sourceVariables,
    calculations,
    sourceData,
    showInduedScores,
    showTransparencyScore,
    showFinancialStrengthScores,
    showEsgScores,
    showSourceData,
    contextModels,
    enableTransparencyScore,
    showEcovadisScores,
    showSecurityRatingScores,
    showCreditsafeScores,
    lang,
    collectiveProceedingSteps
  } = useContext(CalculatorContext);

  const { onVariableChangeCallBack, currentVariable } = useContext(
    VariableContext
  );

  const { selectedTargetFieldCategory } = useContext(TargetFieldContext);

  const getDependencyFreeCalculatedAttributes = useCallback(() => {
    const calculationToCheck = _.find(calculations, {
      id: selectedCalculation?.id,
    });
    const otherCalculations = calculations?.filter(
      (calculation) => calculation.id !== selectedCalculation?.id
    );

    return calculatedAttributes.filter(
      (entry) =>
        !isCalculationReferencedByTargetCalculation(
          calculationToCheck,
          entry,
          otherCalculations
        )
    );
  }, [calculations, selectedCalculation, calculatedAttributes]);

  const getDependencyFreeThirdPartyIndicators = useCallback(() => {
    const calculationToCheck = _.find(calculations, {
      id: selectedCalculation?.id,
    });
    const otherCalculations = calculations?.filter(
      (calculation) => calculation.id !== selectedCalculation?.id
    );

    return thirdPartyIndicators.filter(
      (entry) =>
        !isCalculationReferencedByTargetCalculation(
          calculationToCheck,
          entry,
          otherCalculations
        )
    );
  }, [calculations, selectedCalculation, thirdPartyIndicators]);

  const getDependencyFreeSourceVariables = useCallback(() => {
    const calculationToCheck = _.find(calculations, {
      id: selectedCalculation?.id,
    });
    const otherCalculations = calculations?.filter(
      (calculation) => calculation.id !== selectedCalculation?.id
    );

    return sourceVariables.filter(
      (entry) =>
        !isCalculationReferencedByTargetCalculation(
          calculationToCheck,
          entry,
          otherCalculations
        )
    );
  }, [calculations, selectedCalculation, sourceVariables]);

  const formMethods = useFormContext();
  const { setValue, getValues } = formMethods;
  const targetFieldData = useWatch({ name: `${fieldName}` });

  const getAvailableTargetFields = useCallback(() => {
    let availableTargetFields = [];
    switch (selectedTargetFieldCategory) {
      case TargetFieldCategory.THIRD_PARTY:
        availableTargetFields = _.filter(
          getTargetFieldOptionsForThirdParty(
            countries,
            activities,
            thirdpartyAttributes,
            _.filter(getDependencyFreeCalculatedAttributes(), item => isMatchingThirdPartyTarget(item)),
            _.filter(getDependencyFreeThirdPartyIndicators(), item => isMatchingThirdPartyTarget(item)),
            financialStrengthScores,
            financialStrengthRatings,
            sourceData,
            showInduedScores,
            showTransparencyScore,
            showFinancialStrengthScores,
            showEsgScores,
            showSourceData,
            enableTransparencyScore,
            currentVariable?.type === VariableType.RESULT_FORMULA_ARGUMENT,
            showEcovadisScores,
            showSecurityRatingScores,
            showCreditsafeScores,
            lang,
            collectiveProceedingSteps
          ),
          { fieldNature: targetFieldData?.target_field_nature }
        );
        break;

      case TargetFieldCategory.DOSSIER:
        availableTargetFields = getTargetFieldOptionsForDossier(
          dossierAttributes,
          _.filter(getDependencyFreeCalculatedAttributes(), item => isMatchingDossierTarget(item, targetFieldData?.dossier_type_id)),
          _.filter(getDependencyFreeThirdPartyIndicators(), item => isMatchingDossierTarget(item, targetFieldData?.dossier_type_id)),
          _.filter(getDependencyFreeSourceVariables(), item => isMatchingDossierTarget(item, targetFieldData?.dossier_type_id)),
          currentVariable?.type === VariableType.RESULT_FORMULA_ARGUMENT
        );
        break;

      case TargetFieldCategory.DOCUMENT:
        availableTargetFields = getTargetFieldOptionsForDocument(
          _.find(documentTypes, { value: targetFieldData?.document_type_id })
            ?.properties || [],
          _.find(documentTypes, { value: targetFieldData?.document_type_id })
            ?.ocrExtractedFields || [],
          currentVariable?.type === VariableType.RESULT_FORMULA_ARGUMENT
        );
        break;

      case TargetFieldCategory.SOURCE_VARIABLE:
        availableTargetFields = getTargetFieldOptionsForSourceVariable(
          _.filter(getDependencyFreeSourceVariables(), item => isMatchingThirdPartyTarget(item)),
          currentVariable?.type === VariableType.RESULT_FORMULA_ARGUMENT
        );
        break;

      case TargetFieldCategory.CONTEXT:
        const targetFieldNature = targetFieldData && targetFieldData.target_field_nature;
        if (!targetFieldNature) {
          availableTargetFields = [];
        } else if (ContextDirectField.includes(targetFieldNature)) {
          availableTargetFields = [getTargetFieldOptionsForContextDirectField(targetFieldNature)];
        } else {
          availableTargetFields = getTargetFieldOptionsForContextModels(
            contextModels,
            targetFieldData,
            currentVariable?.type === VariableType.RESULT_FORMULA_ARGUMENT
          );
        }
        break;

      default:
        break;
    }
    return availableTargetFields;
  }, [
    countries,
    activities,
    thirdpartyAttributes,
    dossierAttributes,
    financialStrengthScores,
    financialStrengthRatings,
    sourceData,
    documentTypes,
    showInduedScores,
    showTransparencyScore,
    showFinancialStrengthScores,
    showEsgScores,
    showSourceData,
    enableTransparencyScore,
    getDependencyFreeCalculatedAttributes,
    getDependencyFreeThirdPartyIndicators,
    getDependencyFreeSourceVariables,
    selectedTargetFieldCategory,
    targetFieldData,
    currentVariable,
    showEcovadisScores,
    showSecurityRatingScores,
    showCreditsafeScores,
    lang,
    contextModels,
    collectiveProceedingSteps
  ]);

  const [availableFields, setAvailableFields] = useState(
    getAvailableTargetFields()
  );
  const [
    availableFieldsWithSubFields,
    setAvailableFieldsWithSubFields,
  ] = useState(getFlatSubFieldsOptions(getAvailableTargetFields()));
  const [show, setShow] = useState(false);

  const [availableSubFields, setAvailableSubFields] = useState();

  const getDefaultTargetFieldForExistingCalculation = useCallback(
    (fieldValue) => {
      let entryFinalField = _.find(availableFields, { value: fieldValue });
      let entryFinalFieldWithSubFields = _.find(availableFieldsWithSubFields, {
        value: fieldValue,
      });
      if (entryFinalField) {
        const complexListSubField = getValues(`${fieldName}.complex_list_sub_field`);
        const innerField = getValues(`${fieldName}.inner_field`);
        const targetComplexListSubField = _.find(entryFinalField.subFields, { label: complexListSubField?.field });
        if (entryFinalField.subFields){
          if (targetComplexListSubField) {
            setValue(`${fieldName}.intermediatefield-1`, targetComplexListSubField.value);
          } else if (innerField) {
            setValue(`${fieldName}.intermediatefield-1`, innerField);
          }
          setAvailableSubFields(entryFinalField.subFields);
        }
        setValue(`${fieldName}.intermediatefield-0`, entryFinalField.value);
        setValue(`${fieldName}.field`, entryFinalField.value);
      } else if (entryFinalFieldWithSubFields?.path) {
        const level = entryFinalFieldWithSubFields.path?.length;
        for (var i = 0; i < level; i++) {
          setValue(
            `${fieldName}.intermediatefield-${i}`,
            entryFinalFieldWithSubFields.path[i]
          );
          if (i === 0) {
            let entryFirstSubField = _.find(availableFieldsWithSubFields, {
              value: entryFinalFieldWithSubFields.path[i],
            });
            setAvailableSubFields(
              entryFirstSubField.subFields
                ? entryFirstSubField.subFields
                : undefined
            );
          }
        }
        const innerField = getValues(`${fieldName}.inner_field`);
        if (innerField) {
          setValue(`${fieldName}.intermediatefield-${level}`, innerField);
        }
        setValue(`${fieldName}.field`, entryFinalFieldWithSubFields.value);
      }
    },
    [availableFields, availableFieldsWithSubFields, fieldName, getValues, setValue]
  );

  useEffect(() => {
    setAvailableFields(getAvailableTargetFields());
    setAvailableFieldsWithSubFields(
      getFlatSubFieldsOptions(getAvailableTargetFields())
    );
  }, [getAvailableTargetFields]);

  const clearSubFieldsValues = useCallback(
(initLevel = 1) => {
         const obj = getValues(fieldName) || {};
         Object.keys(obj)
         .filter((k) => k.startsWith("intermediatefield-"))
         .map((k) => ({ key: k, idx: Number(k.replace("intermediatefield-", "")) }))
         .filter(({ idx }) => Number.isFinite(idx) && idx >= initLevel)
         .forEach(({ key }) => {
            setValue(`${fieldName}.${key}`, undefined);
         });
        },
    [fieldName, getValues, setValue]
  );

  useEffect(() => {
    const fieldValue = getValues(`${fieldName}.field`);
    const firsIntermediateValue = getValues(`${fieldName}.intermediatefield-0`);
    //Dans le cas de la modification d'un calcul, si le champs intermediatefield-0 n'a pas encore été rempli,
    // il fautle remplir ainsi que les différents niveaux de intermediatefields
    if (fieldValue && !firsIntermediateValue) {
      getDefaultTargetFieldForExistingCalculation(fieldValue);
    }

    let entryFirstIntermerdiateField = _.find(availableFields, {
      value: getValues(`${fieldName}.intermediatefield-0`),
    });
    // Cas où il y aurait des intermediatefield-0 mais qui ne sont pas compris dans les availableFields
    // Il faut effacer les valeurs de intermediatefield-0 et +
    if (firsIntermediateValue && !entryFirstIntermerdiateField) {
      setValue(`${fieldName}.intermediatefield-0`, undefined);
      setAvailableSubFields(undefined);
      clearSubFieldsValues();
    }

    if (firsIntermediateValue && entryFirstIntermerdiateField && entryFirstIntermerdiateField.subFields && !availableSubFields) {
      setAvailableSubFields(entryFirstIntermerdiateField.subFields);
    }
  }, [
    availableFields,
    availableSubFields,
    availableFieldsWithSubFields,
    clearSubFieldsValues,
    fieldName,
    getDefaultTargetFieldForExistingCalculation,
    getValues,
    setValue
  ]);

  const onFieldChange = useCallback(
    (newValue) => {
        const entry = _.find(availableFields, { value: newValue });
        if (!entry) {
         // Rien trouvé -> on invalide tout et on sort
         setValue(`${fieldName}.intermediatefield-0`, undefined);
         return;
        }
      if (!entry.subFields) {
        setValue(`${fieldName}.field`, entry.value);
        setValue(`${fieldName}.field_type`, entry.type);
        setValue(`${fieldName}.field_sub_type`, entry.subType || ``);
        setValue(`${fieldName}.target_field_nature`, entry.fieldNature);
        setValue(`${fieldName}.inner_field`, undefined);
        setValue(`${fieldName}.complex_list_sub_field`, undefined);
      } else {
        setValue(`${fieldName}.field_type`, undefined);
        setValue(`${fieldName}.field`, undefined);
        setValue(`${fieldName}.inner_field`, undefined);
        setValue(`${fieldName}.complex_list_sub_field`, undefined);
      }
      clearSubFieldsValues();
      setAvailableSubFields(entry.subFields ? entry.subFields : undefined);
    },
    [availableFields, setValue, fieldName, clearSubFieldsValues]
  );

  useEffect(() => {
    const showConditions = {
      [TargetFieldCategory.THIRD_PARTY]: !!targetFieldData?.target_field_nature,
      [TargetFieldCategory.DOSSIER]: !!targetFieldData?.dossier_type_id,
      [TargetFieldCategory.DOCUMENT]: !!targetFieldData?.document_type_id,
      [TargetFieldCategory.SOURCE_VARIABLE]: true,
      [TargetFieldCategory.CONTEXT]: !!targetFieldData?.target_field_nature && !ContextDirectField.includes(targetFieldData?.target_field_nature),
    };

    setShow(showConditions[selectedTargetFieldCategory]);

  }, [selectedTargetFieldCategory, targetFieldData]);

  if (show) {
    return (
      <>
        <div className={classes.variableDivider}> /</div>

        <CalculatorSelectInput
          fieldName={`${fieldName}.intermediatefield-0`}
          label={``}
          isRequired
          placeholder={i18next.t(
            `calculator.dialog.create.placeholder.selectValue`
          )}
          options={getOrderedOptions(availableFields)}
          onChange={(newValue) => {
            onFieldChange(newValue);
            onVariableChangeCallBack(currentVariable, 'intermediatefield-0');
          }}
          validate={() => {
            return null;
          }}
          variant="standard"
          labelVariant="standard"
          className={classes.inputPathVariableItem}
          disabled={readOnly}
        />
        {availableSubFields && (
          <DynamicSubTargetField
            fieldName={`${fieldName}`}
            availableFields={availableSubFields}
            level={1}
            readOnly={readOnly}
          />
        )}
      </>
    );
  } else {
    return <></>;
  }
};

export default InnerTargetField;
